<script>
import moment from 'moment/moment';
import RejectRequestModal from '@/components/inCellVisits/RejectRequestModal.vue';
import InCellVisitsService from '@/services/InCellVisitsService';
import CancelVisitModal from '@/components/inCellVisits/CancelVisitModal.vue';
import IdentityType from '@/components/sharedVisitorDetails/IdentityType.vue';
import VisitorImages from '@/components/visitors/details/sections/VisitorImages.vue';
import AdditionalVisitors from '@/components/inCellVisits/AdditionalVisitors.vue';
import { mapState } from 'vuex';
import RefundInCellKioskItemsModal from '@/components/modals/RefundInCellKioskItemsModal.vue';
import PrisonerVerificationService from '@/services/PrisonerVerificationService';
import FullScreenImageModal from '@/components/modals/FullScreenImageModal.vue';
import RequestedInCellVisitOverview from '@/views/inCellVisits/RequestedInCellVisitOverview.vue';
import InCellCallOverview from '@/views/inCellVisits/InCellCallOverview.vue';
import CS2RecordingAndAuditLog from '@/components/calls/CS2RecordingAndAuditLog.vue';
import { getVisitRequestedDates } from '@/util/helpers';

export default {
  name: 'InCellVisitDetails',
  components: {
    CS2RecordingAndAuditLog,
    InCellCallOverview,
    RequestedInCellVisitOverview,
    RefundInCellKioskItemsModal,
    VisitorImages,
    IdentityType,
    CancelVisitModal,
    RejectRequestModal,
    AdditionalVisitors,
    FullScreenImageModal,
  },
  data() {
    return {
      loading: true,
      visitor: null,
      prisonerVerificationImages: [],
      prisonerVerificationImagesLoading: false,
      prisonerVerificationImagesError: false,
      isPrisonerImageModalVisible: false,
      currentPrisonerImageUrl: '',
      additionalVisitorFields: [
        { label: 'Name', key: 'name' },
        { label: 'Date of birth', key: 'dob' },
      ],
      rejectModalId: 'reject-in-cell-modal',
      cancelModalId: 'cancel-in-cell-modal',
      refundModalId: 'refund-kiosk-in-cell-modal',
    };
  },
  computed: {
    ...mapState({
      visit: state => state.inCellVisits.visit,
      prison: state => state.prison.prison,
      visitorsFirstVisit: state => state.inCellVisits.visitorsFirstVisit,
      additionalVisitors: state => state.inCellVisits.additionalVisitors,
    }),
    showRefundKioskItemsButtons() {
      if (!this.visit || this.visit.status !== 'Completed' || !this.visit.kiosk_items) {
        return false;
      }

      const refundAlreadyProcessed = this.visit.kiosk_items.some(item => {
        const refunded = item.amount_refunded === null ? 0 : item.amount_refunded;
        return refunded > 0;
      });

      return !refundAlreadyProcessed;
    },
    additionalVisitorsButtons() {
      if (!this.visit || !this.visit.status) {
        return { showAddBtn: false, showRemoveBtn: false };
      }

      const { status } = this.visit;

      switch (status) {
        case 'Cancelled':
        case 'Completed':
        case 'Rejected':
          return { showAddBtn: false, showRemoveBtn: false };
        case 'Requested':
          return { showAddBtn: false, showRemoveBtn: true };
        case 'Approved':
        case 'AwaitingPayment':
        case 'Funded':
          return { showAddBtn: true, showRemoveBtn: true };
        default:
          return { showAddBtn: false, showRemoveBtn: false };
      }
    },
    showCancelButtonForOverview() {
      if (!this.visit || !this.visit.status) {
        return false;
      }

      const { status } = this.visit;

      switch (status) {
        case 'Approved':
        case 'Funded':
        case 'AwaitingPayment':
          return true;
        case 'Cancelled':
        case 'Requested':
        case 'Completed':
        case 'Rejected':
          return false;
        default:
          return false;
      }
    },
  },
  async mounted() {
    try {
      if (!this.visit) {
        await this.getVisit();
      }
      if (!this.visit) {
        return;
      }
      await Promise.all([
        this.getVisitorDetails(),
        this.getAdditionalVisitors(),
        this.getPreviousVisits(),
        this.getPrisonerVerificationImages(),
      ]);
    } catch (e) {
      console.error('InCellVisitDetails: mounted() - caught error during data fetch:', e);
      this.$toast.error('Failed to load visit details. Please try again.', {
        toastClassName: ['toast-std', 'warning-toast'],
      });
      this.$router.go(-1);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async getVisitorDetails() {
      try {
        if (!this.visit || !this.visit.visitor || !this.visit.visitor.user_id) {
          return;
        }
        await this.$store.dispatch('visitor/getVisitor', this.visit.visitor.user_id);
        this.visitor = this.$store.state.visitor.visitor;
      } catch (e) {
        console.error('InCellVisitDetails: getVisitorDetails() - error: ', e);
        this.$toast.error('Error getting visitor details', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    async getAdditionalVisitors() {
      try {
        if (!this.visit || !this.visit._id) {
          return;
        }
        await this.$store.dispatch('inCellVisits/getAdditionalVisitors', this.visit._id);
      } catch (e) {
        console.error('InCellVisitDetails: getAdditionalVisitors() - error: ', e);
        this.$toast.error('Error getting additional visitors', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    async getPreviousVisits() {
      try {
        if (!this.visit || !this.visit.visitor || !this.visit.visitor.user_id) {
          return;
        }
        const { data } = await InCellVisitsService.getPreviousInCellVisits(this.visit.visitor.user_id);
        this.$store.commit('inCellVisits/SET_VISITORS_FIRST_VISIT', !(data && Array.isArray(data) && data.length));
      } catch (e) {
        console.error('InCellVisitDetails: getPreviousVisits() - error: ', e);
        throw e;
      }
    },
    async getPrisonerVerificationImages() {
      this.prisonerVerificationImagesLoading = true;
      this.prisonerVerificationImagesError = false;

      try {
        const { prisoner } = this.visit;
        const response = await PrisonerVerificationService.getPrisonerVerificationRecordByDetails(
          prisoner.prisoner_number,
        );

        if (!response || response.verification_status !== 'Verified') {
          this.prisonerVerificationImages = [];
          return;
        }

        const selfieImages = response.selfie_images || [];
        if (selfieImages.length > 0) {
          const latestImage = selfieImages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
          this.prisonerVerificationImages = [latestImage];
        } else {
          this.prisonerVerificationImages = [];
        }
      } catch (e) {
        console.error('Error fetching resident verification images:', e);
        this.prisonerVerificationImagesError = true;
        this.$toast.error(`Error fetching ${this.$options.filters.term('prisoner')} verification images`, {
          toastClassName: ['toast-std', 'warning-toast'],
        });
      } finally {
        this.prisonerVerificationImagesLoading = false;
      }
    },
    async getVisit() {
      try {
        const response = await InCellVisitsService.getVisit(this.$route.params.id);
        await this.$store.dispatch('inCellVisits/setVisit', response.data);
      } catch (e) {
        console.error('InCellVisitDetails: getVisit() - error: ', e);
        this.$toast.error('Error getting in-room call', {
          toastClassName: ['toast-std', 'warning-toast'],
        });
        throw e;
      }
    },
    formatDobAndAge(dob) {
      return `${moment(dob).format('DD/MM/YYYY')} (${moment(new Date()).diff(dob, 'years')})`;
    },
    showModal(modalId) {
      this.$bvModal.show(modalId);
    },
    closeModals() {
      this.$bvModal.hide(this.rejectModalId);
      this.$bvModal.hide(this.cancelModalId);
      this.$bvModal.hide(this.refundModalId);
      this.$bvModal.hide('modal-expand-prisoner-image');
      this.isPrisonerImageModalVisible = false;
    },
    refresh() {
      this.closeModals();
      this.getVisit();
      this.getAdditionalVisitors();
      this.getPrisonerVerificationImages();
    },
    showPrisonerImageModal(imageUrl) {
      this.currentPrisonerImageUrl = imageUrl;
      this.isPrisonerImageModalVisible = true;
      this.$bvModal.show('modal-expand-prisoner-image');
    },
    getVariant() {
      switch (this.visit && this.visit.status) {
        case 'Requested':
          return 'info';
        case 'Rejected':
          return 'danger';
        case 'Completed':
          return 'success';
        case 'Cancelled':
          return 'warning';
        default:
          return 'primary';
      }
    },
    getTotalOfKioskItems() {
      if (this.visit && this.visit.kiosk_items && this.visit.kiosk_items.length > 0) {
        let total = 0;
        this.visit.kiosk_items.forEach(kioskItem => {
          total += (kioskItem.amount * kioskItem.price);
        });
        return total.toFixed(2);
      }
      return '0';
    },
    showRefundModal() {
      if (!this.visit || !this.visit.booked_slot || !this.visit.booked_slot.start_date_time) {
        return;
      }
      const visitDate = new Date(this.visit.booked_slot.start_date_time);
      const currentDate = new Date();
      if (visitDate.getMonth() !== currentDate.getMonth() || visitDate.getFullYear() !== currentDate.getFullYear()) {
        this.$swal({
          title: 'Unable to refund',
          text: 'This visit is not in the current month/year. Please contact staff at Made Purple in order to process a refund.',
          icon: 'info',
          buttons: true,
        });
        return;
      }

      this.showModal(this.refundModalId);
    },
    getSlotDate(visit) {
      const { requestedFor1 } = getVisitRequestedDates(visit);
      return requestedFor1;
    },
  },
}
</script>

<template>
  <div v-if="loading">
    <div class="col text-center mt-5">
      <span class="fas fa-spinner fa-spin" style="font-size: 2em;" />
    </div>
  </div>
  <div v-else>
    <h2 class="font-weight-bolder mb-2 full-width">In-Room Call Details</h2>
    <router-link :to="{ name: 'in-cell-visits-home' }" class="d-inline-block mb-2">
      &laquo; Back to in-room calls
    </router-link>
    <b-row>
      <b-col cols="6">
        <!--Visit Overview-->
        <requested-in-cell-visit-overview v-if="visit && visit.status === 'Requested'" @refresh="refresh" />
        <in-cell-call-overview v-else :show-cancel-button="showCancelButtonForOverview" />

        <!--Visit Details-->
        <b-card>
          <b-card-title>
            Visit Details
          </b-card-title>
          <div class="detail-item">
            Status:
            <b-badge size="sm" :variant="getVariant()">
              {{ visit && visit.status }}
            </b-badge>
          </div>
          <div v-if="visit && visit.status === 'Cancelled'" class="detail-item">
            {{ visit.status }} by:
            <b>{{ visit && visit.cancelled_by }}</b>
          </div>
          <div class="detail-item">
            Location:
            <b>{{ visit && visit.prison && visit.prison.name }}</b>
          </div>
          <div class="detail-item">
            Requested on:
            <b>{{ visit && visit.created_at | formatDateTime }}</b>
          </div>
          <div class="detail-item">
            Booked slot:
            <b>{{ getSlotDate(visit) }}</b>
          </div>
          <div class="detail-item">
            {{ 'prisoner' | term(true) }} name:
            <b>{{ visit && visit.prisoner && visit.prisoner.firstname }} {{ visit && visit.prisoner && visit.prisoner.lastname }}</b>
          </div>
          <div class="detail-item">
            {{ 'prisoner' | term(true) }} ID:
            <b>{{ visit && visit.prisoner && visit.prisoner.prisoner_number }}</b>
          </div>
          <div class="detail-item">
            Relationship to {{ 'prisoner' | term(false) }}:
            <b>{{ visit && visit.prisoner && visit.prisoner.relationship_to_booker }}</b>
          </div>
        </b-card>

        <!--Additional Visitors-->
        <additional-visitors
          :show-add-btn="additionalVisitorsButtons.showAddBtn"
          :show-remove-btn="additionalVisitorsButtons.showRemoveBtn"
          :additional-visitors="additionalVisitors"
          @refresh="refresh"
        />

        <!--Kiosk Items-->
        <b-card v-if="visit && visit.kiosk_items && visit.kiosk_items.length > 0">
          <b-card-title class="d-flex align-items-center">
            Kiosk Items
            <div class="ui-spacer" />
            <b-btn
              v-if="showRefundKioskItemsButtons"
              size="sm"
              variant="warning"
              @click="showRefundModal"
            >
              Refund items
            </b-btn>
          </b-card-title>
          <section class="mb-2">
            <div v-for="item in (visit && visit.kiosk_items)" :key="item && item._id" class="kiosk-item-row d-flex justify-content-between">
              <p class="mb-0">{{ item && item.name }}</p>
              <p class="mb-0">
                <span class="badge badge-success badge-pill mr5">{{ item && item.amount }} x {{ $options.filters.regionCurrency() }}{{ item && item.price && item.price.toFixed(2) }}</span>
                <span class="badge badge-primary badge-pill">Total: {{ $options.filters.regionCurrency() }}{{ item && item.amount && item.price && (item.amount * item.price).toFixed(2) }}</span>
                <span v-if="item && item.amount_refunded > 0" class="badge badge-warning badge-pill ml-1">
                  Refunded: {{ item.amount_refunded }} items ({{ $options.filters.regionCurrency() }}{{ item && item.amount_refunded && item.price && (item.amount_refunded * item.price).toFixed(2) }})
                </span>
              </p>
            </div>
          </section>
          <section>
            <p class="detail-item mb-0" cy="kiosk-total">Kiosk Total: <b>{{ $options.filters.regionCurrency() }}{{ getTotalOfKioskItems() }}</b></p>
          </section>
        </b-card>

      </b-col>

      <!--Verification Images-->
      <b-col cols="6">
        <b-card>
          <b-card-title>
            {{ 'prisoner' | term(true) }} Verification Images
          </b-card-title>
          <div v-if="prisonerVerificationImagesLoading" class="text-center my-3">
            <span class="fas fa-spinner fa-spin" style="font-size: 1.5em;" /> Loading images...
          </div>
          <b-alert v-else-if="prisonerVerificationImagesError" show variant="danger">
            Error loading {{ 'prisoner' | term(false) }} verification images.
          </b-alert>
          <div v-else-if="prisonerVerificationImages.length > 0">
            <div
              v-for="imageRecord in prisonerVerificationImages"
              :key="imageRecord._id"
              class="d-flex flex-column align-items-center"
            >
              <img
                :src="imageRecord.url"
                alt="Resident Verification"
                class="prisoner-image mb-3"
                style="max-width: 150px; height: auto; cursor: pointer;"
                @click="showPrisonerImageModal(imageRecord.url)"
              >
              <div class="detail-item mt-1 w-100">
                <span>Uploaded:</span>
                <b>{{ imageRecord.created_at | formatDate }}</b>
              </div>
            </div>
          </div>
          <p v-else class="text-center">No {{ 'prisoner' | term(false) }} verification images available.</p>
        </b-card>

        <!--Visitor Details-->
        <b-card>
          <b-card-title>
            Visitor Details
          </b-card-title>

          <!--ID Section-->
          <visitor-images />
          <identity-type :person="visitor" />

          <!--Basic Info-->
          <div class="detail-item">
            Name:
            <b>{{ visit && visit.visitor && visit.visitor.firstname }} {{ visit && visit.visitor && visit.visitor.lastname }}</b>
          </div>
          <div class="detail-item">
            Email:
            <b>{{ visit && visit.visitor && visit.visitor.email }}</b>
          </div>
          <div class="detail-item">
            Phone number:
            <b>{{ visit && visit.visitor && visit.visitor.phone_number }}</b>
          </div>
          <div class="detail-item">
            D.O.B. & age:
            <b>{{ formatDobAndAge(visitor && visitor.dob) }}</b>
          </div>
          <div class="detail-item">
            Address line 1:
            <b>{{ visitor && visitor.address && visitor.address.address1 }}</b>
          </div>
          <div class="detail-item">
            Address line 2:
            <b>{{ visitor && visitor.address && visitor.address.address2 }}</b>
          </div>
          <div v-if="visitor && visitor.address && visitor.address.address3" class="detail-item">
            Address line 3:
            <b>{{ visitor.address.address3 }}</b>
          </div>
          <div class="detail-item">
            City/Town:
            <b>{{ visitor && visitor.address && visitor.address.city_town }}</b>
          </div>
          <div class="detail-item">
            Region:
            <b>{{ visitor && visitor.address && visitor.address.region }}</b>
          </div>
          <div class="detail-item">
            Postcode:
            <b>{{ visitor && visitor.address && visitor.address.zip_postal_code }}</b>
          </div>
          <div class="detail-item">
            Country code:
            <b>{{ visitor && visitor.address && visitor.address.country }}</b>
          </div>
        </b-card>
      </b-col>

      <b-col v-if="visit && visit.status === 'Completed'" cols="12">
        <CS2RecordingAndAuditLog
          :visit="visit"
          :purple-scribe-enabled="!!prison.purple_scribe_enabled"
        />
      </b-col>
    </b-row>

    <b-modal :id="rejectModalId" hide-footer>
      <reject-request-modal :visit="visit" @close="closeModals" @refresh="refresh" />
    </b-modal>

    <b-modal :id="cancelModalId" hide-footer>
      <cancel-visit-modal :visit="visit" @close="closeModals" @refresh="refresh" />
    </b-modal>

    <refund-in-cell-kiosk-items-modal
      :visit-id="visit._id"
      :kiosk-items="visit.kiosk_items"
      @close="closeModals"
      @refresh="refresh"
    />

    <b-modal id="modal-expand-prisoner-image" hide-footer size="xl">
      <full-screen-image-modal v-if="isPrisonerImageModalVisible" :image-url="currentPrisonerImageUrl" />
    </b-modal>
  </div>
</template>

<style lang="scss" scoped>
.prisoner-image {
  border: none;
}

.detail-item {
  display: flex;
  justify-content: space-between;
}

.kiosk-item-row {
  border: 2px solid #ebe9f1;
  border-left: none;
  border-right: none;
  padding: 10px 0;

  &:nth-child(2) {
    border-top: none;
  }
}
</style>
